/**
 * Applies Gravity Forms conditional logic in the browser.
 *
 * The theme loads none of Gravity Forms' own scripts, so functions.php prints
 * each field's rules as `data-wgm-logic`, and this shows or hides the field as
 * the answers change. The server applies the same rules on submit: a field
 * hidden here is neither validated nor saved.
 *
 * A form that changes what is shown dispatches `wgm:logic` on itself.
 */
( function() {
	document.querySelectorAll( '.gform_wrapper form' ).forEach( setup );

	/**
	 * Watch one form.
	 *
	 * @param {HTMLFormElement} form The form.
	 */
	function setup( form ) {
		const fields = Array.from( form.querySelectorAll( '[data-wgm-logic]' ) );

		if ( ! fields.length ) {
			return;
		}

		const rules = new Map();

		fields.forEach( function( field ) {
			try {
				rules.set( field, JSON.parse( field.dataset.wgmLogic ) );
			} catch ( error ) {
				// Leave a field with unreadable rules visible.
			}
		} );

		function update() {
			let changed = false;

			rules.forEach( function( logic, field ) {
				const show = matches( form, logic ) === ( 'hide' !== logic.actionType );

				if ( field.hidden === show ) {
					field.hidden = ! show;
					changed = true;
				}
			} );

			if ( changed ) {
				form.dispatchEvent( new CustomEvent( 'wgm:logic', { bubbles: true } ) );
			}
		}

		form.addEventListener( 'change', update );
		form.addEventListener( 'input', update );
		update();
	}

	/**
	 * Whether a field's rules hold.
	 *
	 * @param {HTMLFormElement} form  The form.
	 * @param {Object}          logic Gravity Forms conditional logic.
	 * @return {boolean} True when the rules hold.
	 */
	function matches( form, logic ) {
		const results = ( logic.rules || [] ).map( function( rule ) {
			return test( values( form, String( rule.fieldId ) ), rule );
		} );

		return 'any' === logic.logicType ? results.some( Boolean ) : results.every( Boolean );
	}

	/**
	 * What a field currently holds.
	 *
	 * @param {HTMLFormElement} form    The form.
	 * @param {string}          fieldId A field ID, or a sub-input's such as `3.1`.
	 * @return {string[]} One entry per chosen or typed value; `['']` when there is none.
	 */
	function values( form, fieldId ) {
		const own = fieldId.includes( '.' )
			? [ 'input_' + fieldId ]
			: [ 'input_' + fieldId, 'input_' + fieldId + '[]' ];

		const found = Array.from( form.elements )
			.filter( function( element ) {
				return element.name && ( own.includes( element.name ) || ( ! fieldId.includes( '.' ) && element.name.startsWith( 'input_' + fieldId + '.' ) ) );
			} )
			.filter( function( element ) {
				return ! [ 'radio', 'checkbox' ].includes( element.type ) || element.checked;
			} )
			.flatMap( function( element ) {
				return element.multiple
					? Array.from( element.selectedOptions, function( option ) {
						return option.value;
					} )
					: [ element.value ];
			} );

		return found.length ? found : [ '' ];
	}

	/**
	 * Whether one rule holds, comparing as Gravity Forms does: case-insensitive,
	 * and true when any of the field's values satisfies it.
	 *
	 * @param {string[]} fieldValues What the field holds.
	 * @param {Object}   rule        `{ operator, value }`.
	 * @return {boolean} True when the rule holds.
	 */
	function test( fieldValues, rule ) {
		const target = String( rule.value || '' ).toLowerCase();
		const list = fieldValues.map( function( value ) {
			return String( value ).toLowerCase();
		} );

		switch ( rule.operator ) {
			case 'isnot':
				return ! list.includes( target );
			case 'contains':
				return list.some( function( value ) {
					return value.includes( target );
				} );
			case 'starts_with':
				return list.some( function( value ) {
					return value.startsWith( target );
				} );
			case 'ends_with':
				return list.some( function( value ) {
					return value.endsWith( target );
				} );
			case '>':
				return list.some( function( value ) {
					return parseFloat( value ) > parseFloat( target );
				} );
			case '<':
				return list.some( function( value ) {
					return parseFloat( value ) < parseFloat( target );
				} );
			default:
				return list.includes( target );
		}
	}
}() );
